/**
 * Order Detail Modal Component
 *
 * Shows full order context with stock levels, routing options, and actions.
 * Uses Sheet (slides from right) for better tablet UX.
 *
 * Features:
 * - Order header with minimized PII (customer initials, city only)
 * - Line items table with OOS highlighting
 * - Stock by location grid
 * - Actions: Reroute, Wait for Inventory, Remove from Flow, Notify CX
 * - Event history (collapsible)
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  MapPin,
  Package,
  Clock,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Bell,
  Hourglass,
  Check,
  User,
  Calendar,
  Tag,
  ExternalLink,
  Truck,
  Pause,
  RotateCcw,
  Ban,
  GitBranch,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { trpc } from '@/lib/trpc';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { useLocations } from '@/features/locations/hooks/useLocations';
import { canOpenResolveBoard } from '@/lib/roles';
import {
  type OrderDetail,
  type FulfillmentSuggestion,
  type ProductionIssue,
  isOOSIssue,
} from '../types';
import { NotifyCXDialog } from './NotifyCXDialog';
import { resolveContactAccess, rowBelongsToCallerOrg, type ContactAccess } from '../contactAccess';
import { useSendBackToProduction } from '../hooks/useOnHold';

interface OrderDetailModalProps {
  issueId: string | null;
  issue?: ProductionIssue | null;
  open: boolean;
  onClose: () => void;
  /**
   * 'oos' (default): load detail from an issue and show the OOS action set.
   * 'on_hold': the On Hold tab — load detail directly from prodOrderId (no
   * issue) and show on-hold actions (Send back + Cancel).
   */
  context?: 'oos' | 'on_hold';
  /** Required when context==='on_hold': the prod_order to load detail for. */
  prodOrderId?: string | null;
  /**
   * On-hold only: whether to offer "Send back to production". Mirrors the card's
   * Send-back visibility (operator hold, not Shopify-held / not auto-resume).
   */
  canSendBack?: boolean;
}

export function OrderDetailModal({
  issueId,
  issue,
  open,
  onClose,
  context = 'oos',
  prodOrderId = null,
  canSendBack = false,
}: OrderDetailModalProps) {
  const { t } = useTranslation('production');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const isOnHold = context === 'on_hold';
  const { role, can, isAdmin, isWarehouseCoordinator } = useRole();
  const { hidesProduction } = useOrgRoleSurfaces();
  // Moving an order to another floor. The server refuses it without
  // orders.resolve_locations (`issues.route` checks it first thing, and so does
  // the resolver board), so nothing that starts a route is rendered without it:
  // the destination list, the suggestion's "Route to ..." button, the Move
  // action and the "Resolve locations" entry. Warehouse Staff can open this
  // sheet (the read tier admits them) and keeps what the server lets them do
  // here: send a held order back to production, and the Shopify cancel link.
  const canRoute = can('orders.resolve_locations');
  // Free location choice for Move: routing_suggestions only lists locations
  // that HAVE stock, so a held order with no stock anywhere offered the
  // operator nowhere to go. `locations` is the selectable list (active,
  // delegated rows already dropped) — never allLocations: nothing is picked or
  // held at a shadow row.
  const { locations: selectableLocations } = useLocations();

  // Fetch order detail. OOS keys off the issue; On Hold keys off the prod_order
  // directly (those rows have no issue). Exactly one query is enabled at a time.
  const issueDetailQuery = trpc.productionOrders.issues.getOrderDetail.useQuery(
    { issue_id: issueId! },
    {
      enabled: open && !isOnHold && !!issueId,
      staleTime: 30_000,
    }
  );
  const prodOrderDetailQuery =
    trpc.productionOrders.issues.getOrderDetailByProdOrderId.useQuery(
      { prod_order_id: prodOrderId! },
      {
        enabled: open && isOnHold && !!prodOrderId,
        staleTime: 30_000,
      }
    );
  const orderDetail = isOnHold ? prodOrderDetailQuery.data : issueDetailQuery.data;
  const isLoading = isOnHold
    ? prodOrderDetailQuery.isLoading
    : issueDetailQuery.isLoading;
  const isError = isOnHold
    ? prodOrderDetailQuery.isError
    : issueDetailQuery.isError;

  // Everywhere else the order could go: the selectable locations minus where it
  // already is, minus the ones the stock suggestions above already list.
  const otherLocations = useMemo(() => {
    const suggested = new Set(
      (orderDetail?.routing_suggestions ?? []).map((sug) => sug.location_id)
    );
    return selectableLocations
      .filter((loc) => loc.id !== orderDetail?.location_id && !suggested.has(loc.id))
      .map((loc) => ({ id: loc.id, name: loc.name }));
  }, [selectableLocations, orderDetail?.routing_suggestions, orderDetail?.location_id]);

  // BLU-563: route to the split-order RESOLVER (assignment board). DESIGN.md §10
  // bans a Dialog on a Sheet, so CLOSE this sheet first, then navigate (no
  // stacked overlay). Keyed off the prod_order id (orderDetail.id) - the
  // resolver expands it to the whole order server-side.
  const handleResolveLocations = () => {
    if (!orderDetail?.id) return;
    handleOpenChange(false);
    // Carry where the operator came from, so the board's back link returns them
    // to the list they were working rather than the Split Orders tab.
    navigate(`/production/issues/resolve/${orderDetail.id}?from=${isOnHold ? 'on-hold' : 'oos'}`);
  };

  // Live-fetch the issue itself so metadata-driven UI (CX history, awaiting
  // banner, already-contacted state) updates after a mutation closes the inner
  // dialog without forcing the user to refresh. The `issue` prop is captured
  // at click time on the parent list and would otherwise stay stale.
  const { data: liveIssue } = trpc.productionOrders.issues.getById.useQuery(
    { issue_id: issueId! },
    {
      enabled: open && !!issueId,
      staleTime: 30_000,
    }
  );
  const currentIssue = (liveIssue ?? issue) as ProductionIssue | null | undefined;

  // Which "contact the customer" modes this user may use ON THIS ROW. The email
  // is coordinator-only server-side (notifyCX has no permission of its own, so
  // the role list of its tier is mirrored here), its tier admits only an org
  // that runs a floor, and it is refused on another org's row; so it is offered
  // only when all three hold. The manual mark is open to holders of
  // orders.status_override, which includes CX, and is allowed on a client's row
  // and for a pure client. See contactAccess.ts.
  const contactAccess = resolveContactAccess({
    canMarkContacted: can('orders.status_override'),
    canSendEmail: isAdmin() || isWarehouseCoordinator(),
    isOwnOrgRow: rowBelongsToCallerOrg(organization?.id, currentIssue?.organization_id),
    // `hidesProduction` is true only once the server has confirmed the org owns
    // no non-delegated location: the exact twin of the email tier's admission.
    orgOperatesFloor: !hidesProduction,
  });

  // Mutations
  const utils = trpc.useUtils();
  const routeMutation = trpc.productionOrders.issues.route.useMutation({
    onSuccess: (data, variables) => {
      utils.productionOrders.issues.invalidate();
      // Find the location name from routing suggestions
      const locationName = orderDetail?.routing_suggestions.find(
        s => s.location_id === variables.target_location_id
      )?.location_name;
      const typed = data as {
        shopify_updated?: boolean;
        unmoved?: Array<{ sku: string; quantity: number; reason: string }>;
      };
      const shopifyUpdated = typed?.shopify_updated ?? false;
      // On-Hold has no issue to "resolve" — only append the resolved suffix on the
      // OOS path; on-hold shows just "Routed to X" (+ Shopify-updated when relevant).
      const suffix = shopifyUpdated
        ? t('issues.orderDetail.shopifyUpdated')
        : isOnHold
          ? ''
          : t('issues.orderDetail.allIssuesResolved');
      toast.success(
        locationName
          ? t('issues.orderDetail.routedTo', { location: locationName }) + suffix
          : t('issues.orderDetail.routed') + suffix
      );
      // Phase 57-07: committed-stock rows that could not be repointed to the
      // target are left at origin. Surface as a PERSISTENT warning (no
      // auto-dismiss) — distinct from the auto-dismissing success toast above —
      // fired BEFORE the sheet closes so the operator can act on it.
      const unmoved = typed?.unmoved ?? [];
      if (unmoved.length > 0) {
        const skuList = unmoved.map(u => u.sku).join(', ');
        toast.warning(
          t('issues.routing.partialMoveTitle', { count: unmoved.length }),
          {
            description: t('issues.routing.partialMoveDesc', { skus: skuList }),
            duration: Infinity,
          }
        );
      }
      onClose();
    },
    onError: (err) => {
      toast.error(err.message || t('issues.routing.failed'));
    },
  });
  const awaitMutation =
    trpc.productionOrders.issues.setAwaitingInventory.useMutation({
      onSuccess: () => {
        utils.productionOrders.issues.invalidate();
        toast.success(t('issues.orderDetail.markedAwaiting'));
        onClose();
      },
      onError: (err) => {
        toast.error(err.message || t('issues.orderDetail.awaitingFailed'));
      },
    });
  const unresolveMutation = trpc.productionOrders.issues.unresolve.useMutation({
    onSuccess: () => {
      utils.productionOrders.issues.invalidate();
      // BLU-336: Unresolving restores order to queue — refresh Ready tab
      utils.productionOrders.getOrderQueue.invalidate();
      utils.productionOrders.getQueueCounts.invalidate();
      toast.success(t('issues.orderDetail.restoredToQueue'));
    },
    onError: (err) => {
      toast.error(err.message || t('issues.orderDetail.undoFailed'));
    },
  });
  const resolveMutation = trpc.productionOrders.issues.resolve.useMutation({
    onSuccess: (data, variables) => {
      utils.productionOrders.issues.invalidate();
      // BLU-336: Resolved orders now appear in Ready to Batch tab
      utils.productionOrders.getOrderQueue.invalidate();
      utils.productionOrders.getQueueCounts.invalidate();
      if (variables.resolution_method === 'manual') {
        toast.success(t('issues.resolve.resolvedRequeued'));
      } else {
        // Show undo toast for Remove from Flow
        toast.success(t('issues.orderDetail.removedFromProduction'), {
          action: {
            label: t('issues.orderDetail.undo'),
            onClick: () => unresolveMutation.mutate({ issue_id: data.id }),
          },
          duration: 8000,
        });
      }
      onClose();
    },
    onError: (err) => {
      toast.error(err.message || t('issues.resolve.failed'));
    },
  });

  // Local state
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null
  );
  const [routeNotes, setRouteNotes] = useState('');
  const [awaitNotes, setAwaitNotes] = useState('');
  const [showRouteConfirm, setShowRouteConfirm] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  // "Put on hold" — let the operator opt into auto-resume when stock returns.
  const [holdAutoResume, setHoldAutoResume] = useState(false);
  // For an in-production order: clear it out (cancel in-flight garments) vs keep in bins.
  const [clearProduction, setClearProduction] = useState(false);
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [showNotifyCXDialog, setShowNotifyCXDialog] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // On Hold tab: confirm the Shopify hand-off cancel.
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  /**
   * Everything the operator chose or typed for ONE order. This component stays
   * mounted while its sheet is closed (every mount site renders it
   * unconditionally and flips `open`), so none of it may outlive the order it
   * was entered for: a destination, a note or a ticked "clear production" box
   * left over from order A would be two clicks from being applied to order B.
   */
  const resetSheetState = () => {
    setSelectedLocationId(null);
    setRouteNotes('');
    setAwaitNotes('');
    setShowRouteConfirm(false);
    setShowRemoveConfirm(false);
    setHoldAutoResume(false);
    setClearProduction(false);
    setShowResolveConfirm(false);
    setShowNotifyCXDialog(false);
    setHistoryOpen(false);
    setShowCancelConfirm(false);
  };

  // The ONE place the reset runs: whenever the sheet's identity changes. Closed
  // is its own identity, so every close path (a finished route, hold, wait or
  // send-back, Cancel, Escape, the overlay, a parent that just flips `open`)
  // resets on the way out, and opening for a different order resets even when
  // the sheet was never closed in between. Keyed on props rather than called
  // from each handler so a close path added later cannot forget it. State is
  // adjusted during render (not in an effect) so no frame is ever painted with
  // the previous order's choices.
  const sheetIdentity = open ? `${context}|${issueId ?? ''}|${prodOrderId ?? ''}` : null;
  const [seenSheetIdentity, setSeenSheetIdentity] = useState(sheetIdentity);
  if (seenSheetIdentity !== sheetIdentity) {
    setSeenSheetIdentity(sheetIdentity);
    resetSheetState();
  }

  // On Hold tab: send the order back into production (reuses the list hook).
  const sendBackMutation = useSendBackToProduction();

  // Check if issue is awaiting inventory (read from live issue so the banner
  // collapses immediately after the action posts, without a manual refresh).
  const isAwaitingInventory =
    currentIssue?.metadata &&
    typeof currentIssue.metadata === 'object' &&
    'awaiting_inventory' in currentIssue.metadata &&
    currentIssue.metadata.awaiting_inventory === true;

  // Handlers
  const handleRouteOrder = () => {
    if (!selectedLocationId) return;
    // On-Hold orders have no prod_issue — route by prod_order_id. OOS routes by
    // issue_id (which also marks the issue ROUTED). The backend accepts either.
    if (isOnHold) {
      if (!prodOrderId) return;
      routeMutation.mutate({
        prod_order_id: prodOrderId,
        target_location_id: selectedLocationId,
        notes: routeNotes || undefined,
      });
    } else {
      if (!issueId) return;
      routeMutation.mutate({
        issue_id: issueId,
        target_location_id: selectedLocationId,
        notes: routeNotes || undefined,
      });
    }
  };

  // Phase 57-07: route to the smart-suggested location — selects it (so the
  // route-confirm + mutation see the right target) then opens the confirm dialog.
  const handleRouteSuggested = (locationId: string) => {
    setSelectedLocationId(locationId);
    setShowRouteConfirm(true);
  };

  const handleSetAwaiting = () => {
    if (!issueId) return;
    awaitMutation.mutate({
      issue_id: issueId,
      notes: awaitNotes || undefined,
    });
  };

  const handleResolveRequeue = () => {
    if (!issueId) return;
    // For print file issues, show confirmation first
    if (issue?.issue_type === 'PRINT_FILE_ISSUE') {
      setShowResolveConfirm(true);
      return;
    }
    resolveMutation.mutate({
      issue_id: issueId,
      resolution_method: 'manual',
      notes: 'Resolved manually via Issues screen',
    });
  };

  const handleConfirmResolve = () => {
    if (!issueId) return;
    resolveMutation.mutate({
      issue_id: issueId,
      resolution_method: 'manual',
      notes: 'Print file issue resolved via Issues screen',
    });
  };

  const handleRemoveFromFlow = () => {
    if (!issueId) return;
    resolveMutation.mutate({
      issue_id: issueId,
      resolution_method: 'cancelled',
      notes: 'Put on hold via Issues screen',
      // If the operator opted in, the order auto-resumes once stock returns
      // (waiting_for_stock); otherwise it's a manual hold they send back themselves.
      hold_reason: holdAutoResume ? 'waiting_for_stock' : 'manual',
      hold_auto_resume: holdAutoResume,
      // Only meaningful for an in-production order; the backend ignores it pre-batch.
      hold_clear_production:
        !!orderDetail && !['NEW', 'WAITING_STOCK'].includes(orderDetail.production_status)
          ? clearProduction
          : false,
    });
  };

  const handleNotifyCX = () => {
    if (!currentIssue || !orderDetail) return;
    if (!contactAccess.showEntry) return;
    if (!isOOSIssue(currentIssue.issue_type)) {
      toast.info(t('issues.orderDetail.cxOnlyOos'));
      return;
    }
    // BLU-386: open the dialog regardless of customer_email — the manual-mark
    // mode inside the dialog works without an email. Send-email mode still
    // requires an email and the dialog disables the Send button when missing.
    setShowNotifyCXDialog(true);
  };

  // After contacting the customer, offer to park the order on hold while we wait for
  // their reply (reason=waiting_for_customer, no auto-resume).
  //
  // isStoreOwner seam (3PL): contacting the END customer (and a future "Cancel in
  // Shopify") are own-store operator actions — store owner = prod_orders.organization_id,
  // fulfiller = locations.owner_organization_id. When a store-less 3PL fulfils another
  // org's order, this should instead notify the owning client org (future work; see the
  // 3PL seed prompt in .planning). Today every order's org owns its store → contact direct.
  const handleContactedOfferHold = () => {
    if (!issueId) return;
    toast(t('issues.orderDetail.holdAfterContactTitle'), {
      description: t('issues.orderDetail.holdAfterContactDesc'),
      action: {
        label: t('issues.orderDetail.holdAfterContactAction'),
        onClick: () =>
          resolveMutation.mutate({
            issue_id: issueId,
            resolution_method: 'cancelled',
            notes: 'Put on hold (waiting for customer) after contact',
            hold_reason: 'waiting_for_customer',
            hold_auto_resume: false,
          }),
      },
      duration: 10000,
    });
  };

  // Closing only tells the parent. The state reset is not done here: it hangs
  // off the sheet's identity (see `resetSheetState` above), which is what makes
  // it run for the close paths that never come through this handler.
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose();
  };

  // On Hold tab: send back into production, then close on success.
  const handleSendBackToProduction = () => {
    if (!prodOrderId) return;
    sendBackMutation.mutate(
      { prod_order_id: prodOrderId },
      {
        onSuccess: (data) => {
          if (data.success) {
            toast.success(t('issues.onHold.sentBack'));
            handleOpenChange(false);
          } else {
            // Guarded no-op (e.g. order was no longer ON_HOLD) — don't fake success.
            toast.error(t('issues.onHold.sendBackFailed'));
          }
        },
        onError: (error) => {
          toast.error(error.message || t('issues.onHold.sendBackFailed'));
        },
      }
    );
  };

  // On Hold tab: deep-link the operator to Shopify to cancel there. We do NOT
  // call Shopify's orderCancel (needs write_orders scope the app lacks, and it
  // is an irreversible refund-moving write); the order-sync cron + the on-hold
  // trigger reconcile the prod_order to CANCELLED afterwards.
  const cancelShopifyUrl = getShopifyAdminOrderUrl(
    organization?.shopify_domain,
    orderDetail?.shopify_order_id
  );

  const deficit = currentIssue?.deficit ?? 0;
  const blockedSku = orderDetail?.blocked_sku ?? currentIssue?.blocked_sku;

  // Phase 57-07: smart-reroute suggestion. Only trust it when the backend marked
  // the data complete (fail-closed); otherwise the per-SKU routing rows below are
  // the source of truth and the suggestion card renders nothing.
  const rawSuggestion = orderDetail?.fulfillment_suggestion ?? null;
  const suggestion: FulfillmentSuggestion | null =
    rawSuggestion && rawSuggestion.dataComplete ? rawSuggestion : null;
  // Split = a single location confirmed to cover some-but-not-all SKUs. Used both
  // for the suggestion card AND to inject a warning into the route-confirm dialog
  // when the operator routes to the suggested (partial) location.
  const isSplitSuggestion =
    !!suggestion &&
    !suggestion.fulfillsAll &&
    suggestion.locationId !== null &&
    suggestion.coveredSkus > 0;
  const selectedTargetIsSplit =
    isSplitSuggestion && selectedLocationId === suggestion?.locationId;

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-2xl overflow-y-auto"
        >
          <SheetHeader className="pb-4">
            <SheetTitle className="text-xl font-bold flex items-center gap-2">
              <Package className="h-5 w-5" aria-hidden="true" />
              {t('issues.orderDetail.orderTitle', { name: orderDetail?.order_name || t('issues.orderDetail.loading') })}
            </SheetTitle>
            <SheetDescription>
              {t('issues.orderDetail.sheetDescription')}
            </SheetDescription>
          </SheetHeader>

          {isLoading ? (
            <LoadingSkeleton />
          ) : isError ? (
            <ErrorState />
          ) : orderDetail ? (
            <div className="space-y-6 pb-6">
              {/* View in Shopify - prominent placement */}
              {(() => {
                const shopifyUrl = getShopifyAdminOrderUrl(organization?.shopify_domain, orderDetail.shopify_order_id);
                return shopifyUrl ? (
                  <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '25ms', animationFillMode: 'both' }}>
                    <Button variant="outline" size="sm" className="gap-1.5" asChild>
                      <a href={shopifyUrl} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-3.5 w-3.5" />
                        {t('issues.splitOrders.viewInShopify')}
                      </a>
                    </Button>
                  </div>
                ) : null;
              })()}

              {/* Customer Info Card - staggered entry */}
              <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '50ms', animationFillMode: 'both' }}>
                <CustomerInfoCard orderDetail={orderDetail} />
              </div>

              {/* Line Items - staggered entry */}
              <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '100ms', animationFillMode: 'both' }}>
                <LineItemsSection
                  lineItems={orderDetail.line_items}
                  blockedSku={blockedSku}
                  blockedSkus={orderDetail.blocked_skus?.map(bs => bs.sku)}
                />
              </div>

              {/* Blocked SKU Info - staggered entry */}
              {orderDetail.blocked_skus && orderDetail.blocked_skus.length > 0 ? (
                <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '150ms', animationFillMode: 'both' }}>
                  <BlockedSkusGroup
                    blockedSkus={orderDetail.blocked_skus}
                    currentLocationId={orderDetail.location_id}
                  />
                </div>
              ) : blockedSku ? (
                <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '150ms', animationFillMode: 'both' }}>
                  <BlockedSkusGroup
                    blockedSkus={[{ sku: blockedSku, deficit, stock_by_location: orderDetail.stock_by_location }]}
                    currentLocationId={orderDetail.location_id}
                  />
                </div>
              ) : null}

              {/* Smart all-SKU reroute suggestion (Phase 57-07) — staggered entry.
                  Renders nothing on incomplete data (fail-closed): the per-SKU
                  routing rows below remain the operator's source of truth. */}
              {suggestion && (
                <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '175ms', animationFillMode: 'both' }}>
                  <FulfillmentSuggestionCard
                    suggestion={suggestion}
                    canRoute={canRoute}
                    onRouteSuggested={handleRouteSuggested}
                    onWaitForStock={handleSetAwaiting}
                    isRoutePending={routeMutation.isPending}
                    isAwaitPending={awaitMutation.isPending}
                  />
                </div>
              )}

              {/* Routing Options - staggered entry. For anyone who may route,
                  always rendered: the stock suggestions are the recommendation,
                  not the whole choice, and a held order frequently has stock
                  nowhere. Not rendered at all for a role the server refuses
                  (the stock-by-location table above stays, as information). */}
              {canRoute && (
                <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '200ms', animationFillMode: 'both' }}>
                  <RoutingSection
                    suggestions={orderDetail.routing_suggestions}
                    otherLocations={otherLocations}
                    selectedLocationId={selectedLocationId}
                    onSelectLocation={setSelectedLocationId}
                    notes={routeNotes}
                    onNotesChange={setRouteNotes}
                  />
                </div>
              )}

              {/* BLU-563: Resolve-locations entry — routes to the resolver
                  assignment board (closes this sheet first; no modal-on-Sheet).
                  BLU-842: shown in the on-hold context too — a held order is
                  resolvable now, and the sheet is where the operator lands from
                  the On Hold list. Gated on the permission the resolver checks
                  AND on the role the board's own RoleGuard admits, so the
                  button never leads to a refusal or a silent redirect. */}
              {orderDetail.id && canRoute && canOpenResolveBoard(role) && (
                <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '225ms', animationFillMode: 'both' }}>
                  <div className="flex items-center justify-between gap-3 rounded-lg border p-4">
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-medium">
                        <GitBranch className="h-4 w-4 text-threadheads-purple" aria-hidden="true" />
                        {t('issues.resolveFlow.entryTitle')}
                      </p>
                      <p className="mt-0.5 text-sm text-muted-foreground">
                        {t('issues.resolveFlow.entryExplainer')}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="shrink-0 gap-1.5"
                      onClick={handleResolveLocations}
                    >
                      <GitBranch className="h-4 w-4" aria-hidden="true" />
                      {t('issues.resolveFlow.entryAction')}
                    </Button>
                  </div>
                </div>
              )}

              <Separator />

              {/* Actions - staggered entry */}
              <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '250ms', animationFillMode: 'both' }}>
                <ActionsSection
                  issueType={currentIssue?.issue_type ?? null}
                  isAwaitingInventory={isAwaitingInventory === true}
                  // A target chosen from EITHER group is a routable target; the
                  // server still refuses an unmapped location and gates on
                  // location access.
                  hasRoutingSuggestion={canRoute && !!selectedLocationId}
                  onRoute={() => setShowRouteConfirm(true)}
                  onResolveRequeue={handleResolveRequeue}
                  onSetAwaiting={handleSetAwaiting}
                  onRemove={() => setShowRemoveConfirm(true)}
                  onNotifyCX={handleNotifyCX}
                  showContactEntry={contactAccess.showEntry}
                  contactEntryLabelKey={contactAccess.entryLabelKey}
                  awaitNotes={awaitNotes}
                  onAwaitNotesChange={setAwaitNotes}
                  isRoutePending={routeMutation.isPending}
                  isResolvePending={resolveMutation.isPending}
                  isAwaitPending={awaitMutation.isPending}
                  isRemovePending={resolveMutation.isPending}
                  isOnHold={isOnHold}
                  canSendBack={canSendBack}
                  canCancel={!!cancelShopifyUrl}
                  onSendBack={handleSendBackToProduction}
                  onCancel={() => setShowCancelConfirm(true)}
                  isSendBackPending={sendBackMutation.isPending}
                />
              </div>

              {/* Event History (collapsible) - staggered entry */}
              <div className="animate-in fade-in-50 slide-in-from-left-2 duration-300" style={{ animationDelay: '300ms', animationFillMode: 'both' }}>
                <EventHistorySection
                  issue={currentIssue}
                  open={historyOpen}
                  onOpenChange={setHistoryOpen}
                />
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Route Confirmation Dialog */}
      <AlertDialog open={showRouteConfirm} onOpenChange={setShowRouteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('issues.orderDetail.routeConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('issues.orderDetail.routeConfirmBase')}
              {isOnHold
                ? organization?.shopify_domain
                  ? ' ' + t('issues.orderDetail.routeConfirmShopifyOnly')
                  : ''
                : organization?.shopify_domain
                  ? ' ' + t('issues.orderDetail.routeConfirmShopify')
                  : ' ' + t('issues.orderDetail.routeConfirmResolved')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Phase 57-07: split-fulfilment warning — the chosen target covers only
              some of the order's SKUs. Shown ABOVE the cleanup warning so the
              operator sees the more consequential message first. */}
          {selectedTargetIsSplit && suggestion && (
            <div className="rounded-md border border-amber-300 bg-amber-100 dark:border-amber-700 dark:bg-amber-950/60 px-4 py-3 text-sm text-amber-900 dark:text-amber-200 flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
              <span>
                {t('issues.routing.splitConfirmWarning', {
                  covered: suggestion.coveredSkus,
                  total: suggestion.totalSkus,
                })}
              </span>
            </div>
          )}
          <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/50 px-4 py-3 text-sm text-amber-900 dark:text-amber-200 flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span>{t('issues.orderDetail.routeConfirmWarning')}</span>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-14">{t('issues.orderDetail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRouteOrder}
              disabled={routeMutation.isPending}
              className="h-14"
            >
              {routeMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <MapPin className="h-4 w-4 mr-2" />
              )}
              {t('issues.routing.routeOrder')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Put on hold Confirmation Dialog (amber — paused, NOT cancelled) */}
      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('issues.orderDetail.removeConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('issues.orderDetail.removeConfirmDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {/* Auto-resume opt-in: pause now, but let the OOS cron send it back when stock returns. */}
          <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 cursor-pointer dark:border-amber-900/40 dark:bg-amber-900/10">
            <Checkbox
              checked={holdAutoResume}
              onCheckedChange={(v) => setHoldAutoResume(v === true)}
              className="mt-0.5"
            />
            <span className="text-sm">
              <span className="font-medium">{t('issues.orderDetail.holdAutoResumeLabel')}</span>
              <span className="block text-muted-foreground">{t('issues.orderDetail.holdAutoResumeHint')}</span>
            </span>
          </label>
          {/* In-production only: clear it out (cancel garments → Cancelled Items) vs keep in bins. */}
          {orderDetail && !['NEW', 'WAITING_STOCK'].includes(orderDetail.production_status) && (
            <label className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 cursor-pointer dark:border-amber-900/40 dark:bg-amber-900/10">
              <Checkbox
                checked={clearProduction}
                onCheckedChange={(v) => setClearProduction(v === true)}
                className="mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium">{t('issues.orderDetail.holdClearLabel')}</span>
                <span className="block text-muted-foreground">{t('issues.orderDetail.holdClearHint')}</span>
              </span>
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="h-14">{t('issues.orderDetail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFromFlow}
              disabled={resolveMutation.isPending}
              className="bg-amber-500 text-white hover:bg-amber-600 h-14"
            >
              {resolveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Pause className="h-4 w-4 mr-2" />
              )}
              {t('issues.orderDetail.removeFromFlow')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel order — Shopify hand-off (deep-link; we never call orderCancel) */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('issues.orderDetail.cancelConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('issues.orderDetail.cancelConfirmDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-14">
              {t('issues.orderDetail.cancelDialogBack')}
            </AlertDialogCancel>
            {cancelShopifyUrl && (
              <AlertDialogAction
                asChild
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90 h-14"
              >
                <a
                  href={cancelShopifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleOpenChange(false)}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {t('issues.orderDetail.cancelInShopify')}
                </a>
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Print File Resolve Confirmation Dialog */}
      <AlertDialog open={showResolveConfirm} onOpenChange={setShowResolveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('issues.orderDetail.resolvePrintFileTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('issues.orderDetail.resolvePrintFileDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-14">{t('issues.orderDetail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmResolve}
              disabled={resolveMutation.isPending}
              className="h-14"
            >
              {resolveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              {t('issues.resolve.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Notify CX Dialog. Keyed by issue so a note typed for one issue is
          never still in the form when the sheet moves to the next. */}
      {currentIssue && orderDetail && contactAccess.showEntry && (
        <NotifyCXDialog
          key={issueId ?? ''}
          open={showNotifyCXDialog}
          onOpenChange={setShowNotifyCXDialog}
          issueId={issueId!}
          issue={currentIssue}
          orderDetail={orderDetail}
          allowedModes={contactAccess.modes}
          onContacted={handleContactedOfferHold}
        />
      )}
    </>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-2/3" />
      </div>
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-24 w-full" />
      <div className="grid grid-cols-2 gap-3">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    </div>
  );
}

function ErrorState() {
  const { t } = useTranslation('production');
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertTriangle className="h-12 w-12 text-destructive" aria-hidden="true" />
      <p className="mt-4 text-lg font-medium">{t('issues.orderDetail.failedToLoad')}</p>
      <p className="text-sm text-muted-foreground">
        {t('issues.orderDetail.tryAgain')}
      </p>
    </div>
  );
}

function CustomerInfoCard({ orderDetail }: { orderDetail: OrderDetail }) {
  const { t } = useTranslation('production');
  const orderDate = orderDetail.order_date
    ? format(new Date(orderDetail.order_date), 'MMM d, yyyy')
    : t('issues.orderDetail.unknownDate');

  // Build shipping address string from non-null parts
  const addressParts = [
    orderDetail.shipping_address1,
    orderDetail.shipping_address2,
  ].filter(Boolean);
  const cityStateZip = [
    orderDetail.shipping_address_city,
    [orderDetail.shipping_address_province, orderDetail.shipping_address_zip]
      .filter(Boolean)
      .join(' '),
  ]
    .filter(Boolean)
    .join(', ');
  if (cityStateZip) addressParts.push(cityStateZip);
  if (orderDetail.shipping_address_country) addressParts.push(orderDetail.shipping_address_country);
  const hasShippingAddress = addressParts.length > 0;

  return (
    <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="font-medium">{orderDetail.customer_name || t('issues.orderDetail.unknownCustomer')}</span>
        </div>
        <Badge variant="outline">{orderDetail.production_status}</Badge>
      </div>
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span className="flex items-center gap-1">
          <Calendar className="h-4 w-4" aria-hidden="true" />
          {orderDate}
        </span>
        <span className="flex items-center gap-1">
          <MapPin className="h-4 w-4" aria-hidden="true" />
          {orderDetail.location_name}
        </span>
      </div>
      {hasShippingAddress && (
        <div className="flex items-start gap-2 text-sm text-muted-foreground pt-1 border-t border-border/50">
          <Truck className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{addressParts.join(', ')}</span>
        </div>
      )}
    </div>
  );
}

function LineItemsSection({
  lineItems,
  blockedSku,
  blockedSkus,
}: {
  lineItems: OrderDetail['line_items'];
  blockedSku: string | null | undefined;
  blockedSkus?: string[];
}) {
  const { t } = useTranslation('production');
  const blockedSkuSet = new Set(blockedSkus ?? (blockedSku ? [blockedSku] : []));

  return (
    <div className="space-y-2">
      <h3 className="font-semibold flex items-center gap-2">
        <Tag className="h-4 w-4" aria-hidden="true" />
        {t('issues.orderDetail.lineItems')}
      </h3>
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th scope="col" className="text-left px-4 py-2 font-medium">{t('issues.orderDetail.product')}</th>
              <th scope="col" className="text-left px-4 py-2 font-medium">{t('issues.orderDetail.sku')}</th>
              <th scope="col" className="text-right px-4 py-2 font-medium">{t('issues.orderDetail.qty')}</th>
              <th scope="col" className="text-center px-4 py-2 font-medium">{t('issues.orderDetail.status')}</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((item, idx) => {
              const isOOS = item.is_oos || blockedSkuSet.has(item.sku);
              return (
                <tr
                  key={item.id}
                  className={isOOS ? 'bg-red-50 dark:bg-red-950/50 text-red-900 dark:text-red-200' : idx % 2 === 0 ? '' : 'bg-muted/20'}
                >
                  <td className="px-4 py-3">{item.product_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{item.sku}</td>
                  <td className="px-4 py-3 text-right">{item.quantity}</td>
                  <td className="px-4 py-3 text-center">
                    {isOOS ? (
                      <Badge
                        variant="destructive"
                        className="text-xs"
                      >
                        OOS
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">
                        {item.status || 'OK'}
                      </Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BlockedSkusGroup({
  blockedSkus,
  currentLocationId,
}: {
  blockedSkus: Array<{
    sku: string;
    deficit: number;
    stock_by_location: OrderDetail['stock_by_location'];
  }>;
  currentLocationId: string;
}) {
  const { t } = useTranslation('production');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const selected = blockedSkus[selectedIdx];
  const multipleSkus = blockedSkus.length > 1;

  if (!selected) return null;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/50 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold text-red-900 dark:text-red-200">
              {t('issues.orderDetail.blockedSku', { count: multipleSkus ? 2 : 1 })}
            </p>
            {!multipleSkus && (
              <p className="font-mono text-sm text-red-800 dark:text-red-300">{selected.sku}</p>
            )}
          </div>
          {multipleSkus ? (
            <Badge variant="destructive" className="text-sm">
              {blockedSkus.length} SKUs
            </Badge>
          ) : selected.deficit > 0 ? (
            <Badge variant="destructive" className="text-sm">
              {t('issues.orderDetail.needUnits', { count: selected.deficit })}
            </Badge>
          ) : null}
        </div>

        {/* SKU selector chips — only shown when multiple */}
        {multipleSkus && (
          <div className="flex flex-wrap gap-2 pt-1">
            {blockedSkus.map((bs, idx) => {
              const isActive = idx === selectedIdx;
              return (
                <button
                  key={bs.sku}
                  type="button"
                  onClick={() => setSelectedIdx(idx)}
                  className={`
                    inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors
                    ${isActive
                      ? 'border-red-400 dark:border-red-600 bg-white dark:bg-red-900/60 text-red-900 dark:text-red-100 shadow-sm'
                      : 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/30 text-red-700 dark:text-red-400 hover:bg-white dark:hover:bg-red-900/40'
                    }
                  `}
                >
                  <span className="font-mono">{bs.sku}</span>
                  {bs.deficit > 0 && (
                    <span className={`rounded px-1 py-0.5 text-[10px] font-semibold ${
                      isActive
                        ? 'bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200'
                        : 'bg-red-100/80 dark:bg-red-900/60 text-red-600 dark:text-red-400'
                    }`}>
                      -{bs.deficit}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Stock by Location for selected SKU */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium text-muted-foreground flex items-center justify-between">
          <span>{t('issues.orderDetail.stockAtLocations')}</span>
          {multipleSkus && (
            <span className="font-mono text-xs">
              {selected.sku}
              {selected.deficit > 0 && (
                <span className="ml-1.5 text-red-600 dark:text-red-400">
                  (need {selected.deficit})
                </span>
              )}
            </span>
          )}
        </h4>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th scope="col" className="text-left px-4 py-2 font-medium">{t('issues.orderDetail.location')}</th>
                <th scope="col" className="text-right px-4 py-2 font-medium">{t('issues.orderDetail.available')}</th>
              </tr>
            </thead>
            <tbody>
              {selected.stock_by_location.map((stock, idx) => {
                const isCurrent = stock.location_id === currentLocationId;
                const hasStock = stock.available_quantity > 0;
                const rowClass = isCurrent
                  ? 'bg-muted/50'
                  : hasStock
                  ? 'bg-green-50 dark:bg-green-950/50'
                  : idx % 2 === 1
                  ? 'bg-muted/20'
                  : '';
                return (
                  <tr key={stock.location_id} className={rowClass}>
                    <td className="px-4 py-2 flex items-center gap-2">
                      {stock.location_name}
                      {isCurrent && (
                        <Badge variant="outline" className="text-xs">
                          {t('issues.orderDetail.current')}
                        </Badge>
                      )}
                    </td>
                    <td
                      className={`px-4 py-2 text-right font-medium ${
                        hasStock ? 'text-green-600' : 'text-red-600'
                      }`}
                    >
                      {stock.available_quantity}
                    </td>
                  </tr>
                );
              })}
              {selected.stock_by_location.length === 0 && (
                <tr>
                  <td
                    colSpan={2}
                    className="px-4 py-3 text-center text-muted-foreground"
                  >
                    {t('issues.orderDetail.noStockInfo')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RoutingSection({
  suggestions,
  otherLocations,
  selectedLocationId,
  onSelectLocation,
  notes,
  onNotesChange,
}: {
  suggestions: OrderDetail['routing_suggestions'];
  /**
   * Every other location the operator may move this order to: active,
   * delegated rows excluded, the order's current location removed, and
   * anything already listed in `suggestions` removed. These carry no stock, so
   * each is labelled as such rather than silently looking equivalent.
   */
  otherLocations: Array<{ id: string; name: string }>;
  selectedLocationId: string | null;
  onSelectLocation: (id: string | null) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
}) {
  const { t } = useTranslation('production');
  return (
    <div className="space-y-3">
      <h3 className="font-semibold flex items-center gap-2">
        <MapPin className="h-4 w-4" aria-hidden="true" />
        {t('issues.orderDetail.routeToLocation')}
      </h3>
      {/* Always controlled: '' is "nothing chosen". Passing undefined would hand
          the choice to Radix's own internal state, which keeps the last pick
          after the sheet's state is reset for another order. */}
      <RadioGroup
        value={selectedLocationId ?? ''}
        onValueChange={onSelectLocation}
        className="space-y-2"
      >
        {suggestions.map((suggestion) => (
          <div
            key={suggestion.location_id}
            className="flex items-center space-x-3 rounded-lg border p-4 min-h-14 hover:bg-muted/50"
          >
            <RadioGroupItem
              value={suggestion.location_id}
              id={suggestion.location_id}
            />
            <Label
              htmlFor={suggestion.location_id}
              className="flex flex-1 cursor-pointer items-center justify-between"
            >
              <span className="font-medium">{suggestion.location_name}</span>
              <span className="flex items-center gap-1 text-sm text-green-600">
                <Package className="h-4 w-4" aria-hidden="true" />
                {t('issues.routing.available', { count: suggestion.available_quantity })}
              </span>
            </Label>
          </div>
        ))}

        {otherLocations.length > 0 && (
          <>
            <p className="pt-2 text-sm font-medium text-muted-foreground">
              {t('issues.routing.otherLocations')}
            </p>
            {otherLocations.map((loc) => (
              <div
                key={loc.id}
                className="flex items-center space-x-3 rounded-lg border p-4 min-h-14 hover:bg-muted/50"
              >
                <RadioGroupItem value={loc.id} id={loc.id} />
                <Label
                  htmlFor={loc.id}
                  className="flex flex-1 cursor-pointer items-center justify-between"
                >
                  <span className="font-medium">{loc.name}</span>
                  <span className="text-sm text-muted-foreground">
                    {t('issues.routing.noStockRecorded')}
                  </span>
                </Label>
              </div>
            ))}
          </>
        )}
      </RadioGroup>

      {suggestions.length === 0 && otherLocations.length === 0 && (
        <div className="rounded-lg border border-dashed bg-muted/30 p-5 text-center text-sm text-muted-foreground">
          {t('issues.routing.noOtherLocations')}
        </div>
      )}

      {selectedLocationId && (
        <div className="space-y-2 pt-2">
          <Label htmlFor="route-notes">{t('issues.routing.notesLabel')}</Label>
          <Textarea
            id="route-notes"
            placeholder={t('issues.routing.notesPlaceholder')}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            maxLength={500}
          />
        </div>
      )}
    </div>
  );
}

function FulfillmentSuggestionCard({
  suggestion,
  canRoute,
  onRouteSuggested,
  onWaitForStock,
  isRoutePending,
  isAwaitPending,
}: {
  suggestion: FulfillmentSuggestion;
  /** False = the card still says where the stock is, with no "Route to" button. */
  canRoute: boolean;
  onRouteSuggested: (locationId: string) => void;
  onWaitForStock: () => void;
  isRoutePending: boolean;
  isAwaitPending: boolean;
}) {
  const { t } = useTranslation('production');

  const hasTarget = suggestion.locationId !== null && suggestion.coveredSkus > 0;
  const locationName = suggestion.locationName ?? '';

  // State 3 — nowhere to route: no single location carries any of the SKUs.
  // Promote the existing "Wait for Stock" affordance.
  if (!hasTarget) {
    return (
      <div className="space-y-3">
        <h3 className="font-semibold flex items-center gap-2">
          <MapPin className="h-4 w-4" aria-hidden="true" />
          {t('issues.orderDetail.fulfilmentSuggestion')}
        </h3>
        <div className="rounded-lg border border-dashed bg-muted/30 p-5 flex flex-col items-center text-center gap-2">
          <Hourglass className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium">{t('issues.routing.noStock')}</p>
          <p className="text-sm text-muted-foreground">{t('issues.routing.noStockDesc')}</p>
          <Button
            variant="secondary"
            className="mt-2 h-11 gap-2"
            onClick={onWaitForStock}
            disabled={isAwaitPending}
          >
            {isAwaitPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Hourglass className="h-4 w-4" />
            )}
            {t('issues.orderDetail.waitForStock')}
          </Button>
        </div>
      </div>
    );
  }

  const fulfillsAll = suggestion.fulfillsAll;
  const missingItems = suggestion.items.filter(i => !i.ok);

  return (
    <div className="space-y-3">
      <h3 className="font-semibold flex items-center gap-2">
        <MapPin className="h-4 w-4" aria-hidden="true" />
        {t('issues.orderDetail.fulfilmentSuggestion')}
      </h3>

      <div
        className={`rounded-lg border p-4 space-y-3 ${
          fulfillsAll
            ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40'
            : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          {/* State pill — reserved token + glyph (colour is never the sole signal) */}
          {fulfillsAll ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-300/80">
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
              {t('issues.routing.fulfilsAll')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-300/80">
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
              {t('issues.routing.coversCount', {
                covered: suggestion.coveredSkus,
                total: suggestion.totalSkus,
              })}
            </span>
          )}
          <span className="text-xs tabular-nums text-muted-foreground">
            {t('issues.routing.skuCount', { count: suggestion.totalSkus })}
          </span>
        </div>

        <p className="text-sm">
          {fulfillsAll
            ? t('issues.routing.fulfilsAllDesc', { location: locationName })
            : t('issues.routing.coversDesc', {
                location: locationName,
                covered: suggestion.coveredSkus,
                total: suggestion.totalSkus,
              })}
        </p>

        {/* Split state — list the SKUs this location cannot cover, with the same
            deficit-chip idiom used by BlockedSkusGroup. */}
        {!fulfillsAll && missingItems.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-amber-900 dark:text-amber-200">
              {t('issues.routing.stillMissing')}
            </p>
            <div className="flex flex-wrap gap-2">
              {missingItems.map(item => (
                <span
                  key={item.sku}
                  className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 dark:border-amber-800 bg-white dark:bg-amber-900/40 px-2.5 py-1.5 text-xs font-medium text-amber-900 dark:text-amber-100"
                >
                  <span className="font-mono">{item.sku}</span>
                  <span className="rounded bg-amber-100 dark:bg-amber-800 px-1 py-0.5 text-[10px] font-semibold tabular-nums text-amber-700 dark:text-amber-200">
                    {item.available}/{item.needed}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        {canRoute && (
          <Button
            variant={fulfillsAll ? 'default' : 'secondary'}
            className="h-11 gap-2 w-full"
            onClick={() => suggestion.locationId && onRouteSuggested(suggestion.locationId)}
            disabled={isRoutePending}
          >
            {isRoutePending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MapPin className="h-4 w-4" />
            )}
            {t('issues.routing.routeToLocation', { location: locationName })}
          </Button>
        )}
      </div>
    </div>
  );
}

function ActionsSection({
  issueType,
  isAwaitingInventory,
  hasRoutingSuggestion,
  onRoute,
  onResolveRequeue,
  onSetAwaiting,
  onRemove,
  onNotifyCX,
  showContactEntry,
  contactEntryLabelKey,
  awaitNotes,
  onAwaitNotesChange,
  isRoutePending,
  isResolvePending,
  isAwaitPending,
  isRemovePending,
  isOnHold,
  canSendBack,
  canCancel,
  onSendBack,
  onCancel,
  isSendBackPending,
}: {
  issueType: string | null;
  isAwaitingInventory: boolean;
  hasRoutingSuggestion: boolean;
  onRoute: () => void;
  onResolveRequeue: () => void;
  onSetAwaiting: () => void;
  onRemove: () => void;
  onNotifyCX: () => void;
  /** The caller may use at least one contact mode (see contactAccess.ts). */
  showContactEntry: boolean;
  /** "Notify CX" for the coordinator tier, "Mark as contacted" for CX. */
  contactEntryLabelKey: ContactAccess['entryLabelKey'];
  awaitNotes: string;
  onAwaitNotesChange: (notes: string) => void;
  isRoutePending: boolean;
  isResolvePending: boolean;
  isAwaitPending: boolean;
  isRemovePending: boolean;
  isOnHold: boolean;
  canSendBack: boolean;
  canCancel: boolean;
  onSendBack: () => void;
  onCancel: () => void;
  isSendBackPending: boolean;
}) {
  const { t } = useTranslation('production');
  const anyPending = isRoutePending || isResolvePending || isAwaitPending || isRemovePending;
  const isOosType = issueType === 'OOS_PRE_BATCH' || issueType === 'INVENTORY_ERROR';
  const isPrintFileType = issueType === 'PRINT_FILE_ISSUE';

  // On Hold tab: a focused action set — Send back into production (when this is
  // an operator hold we can resume) and a destructive Shopify hand-off Cancel.
  if (isOnHold) {
    return (
      <div className="space-y-4">
        <h3 className="font-semibold">{t('issues.orderDetail.actions')}</h3>
        {canSendBack || canCancel || hasRoutingSuggestion ? (
          <div className="flex flex-col gap-3">
            {/* Route to another location — On-Hold parity with OOS. The primary
                action when set: it unblocks a waiting order by moving it to stock.
                Appears once a target is selected in Routing options or via the
                smart suggestion card. */}
            {hasRoutingSuggestion && (
              <Button
                variant="default"
                className="h-14 w-full gap-2"
                onClick={onRoute}
                disabled={isRoutePending || isSendBackPending}
              >
                {isRoutePending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <MapPin className="h-4 w-4" />
                )}
                {t('issues.routing.routeOrder')}
              </Button>
            )}
            {hasRoutingSuggestion && (canSendBack || canCancel) && <Separator />}
            {canSendBack && (
              <Button
                variant={hasRoutingSuggestion ? 'outline' : 'default'}
                className="h-14 w-full gap-2"
                onClick={onSendBack}
                disabled={isSendBackPending || isRoutePending}
              >
                {isSendBackPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4" />
                )}
                {t('issues.onHold.sendBack')}
              </Button>
            )}
            {canSendBack && canCancel && <Separator />}
            {/* Cancel only when we can actually deep-link to Shopify (the cancel
                is a hand-off, not a write) — never a Back-only dead-end dialog. */}
            {canCancel && (
              <Button
                variant="destructive"
                className="h-14 w-full gap-2"
                onClick={onCancel}
                disabled={isSendBackPending || isRoutePending}
              >
                <Ban className="h-4 w-4" />
                {t('issues.orderDetail.cancelOrder')}
              </Button>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('issues.orderDetail.noOnHoldActions')}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">{t('issues.orderDetail.actions')}</h3>

      {/* Awaiting Inventory Status */}
      {isAwaitingInventory && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/50 p-3">
          <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
            {t('issues.orderDetail.awaitingInventory')}
          </span>
        </div>
      )}

      {/* Wait for Inventory Section (if not already waiting) */}
      {!isAwaitingInventory && (
        <div className="space-y-2 rounded-lg border p-4">
          <Label htmlFor="await-notes" className="font-medium">
            {t('issues.orderDetail.waitForInventory')}
          </Label>
          <p className="text-sm text-muted-foreground">
            {t('issues.orderDetail.waitForInventoryDesc')}
          </p>
          <Textarea
            id="await-notes"
            placeholder={t('issues.orderDetail.awaitNotesPlaceholder')}
            value={awaitNotes}
            onChange={(e) => onAwaitNotesChange(e.target.value)}
            maxLength={500}
            className="mt-2"
          />
        </div>
      )}

      {/* Action Buttons - h-14 for warehouse UX, flex-wrap prevents orphan buttons */}
      <div className="flex flex-wrap gap-3">
        {/* Route Button */}
        {hasRoutingSuggestion && (
          <Button
            variant="default"
            className="h-14 gap-2 flex-1 min-w-[140px]"
            onClick={onRoute}
            disabled={anyPending}
          >
            {isRoutePending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <MapPin className="h-4 w-4" />
            )}
            {t('issues.routing.routeOrder')}
          </Button>
        )}

        {/* Resolve — Re-queue (OOS types) or Resolve Issue (print file) — primary action, full width */}
        {(isOosType || isPrintFileType) && (
          <Button
            variant="default"
            className="h-14 gap-2 w-full bg-green-600 hover:bg-green-700 text-white"
            onClick={onResolveRequeue}
            disabled={anyPending}
          >
            {isResolvePending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {isPrintFileType ? t('issues.orderDetail.resolveIssue') : t('issues.orderDetail.resolveRequeue')}
          </Button>
        )}

        {/* Wait for Inventory Button */}
        {!isAwaitingInventory && (
          <Button
            variant="secondary"
            className="h-14 gap-2 flex-1 min-w-[140px]"
            onClick={onSetAwaiting}
            disabled={anyPending}
          >
            {isAwaitPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Hourglass className="h-4 w-4" />
            )}
            {t('issues.orderDetail.waitForStock')}
          </Button>
        )}

        {/* Contact entry (OOS types only, and only for a user who can use at
            least one mode). Coordinators see "Notify CX" and both modes; CX
            sees "Mark as contacted" and lands on the manual form; Warehouse
            Staff sees nothing, where there used to be a button that 403'd. */}
        {isOosType && showContactEntry && (
          <Button
            variant="outline"
            className="h-14 gap-2 flex-1 min-w-[140px]"
            onClick={onNotifyCX}
            disabled={anyPending}
          >
            <Bell className="h-4 w-4" />
            {t(contactEntryLabelKey)}
          </Button>
        )}

        {/* Put on Hold Button (amber — paused, NOT cancelled; matches the confirm dialog) */}
        <Button
          variant="ghost"
          className="h-14 gap-2 flex-1 min-w-[140px] text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-500 dark:hover:text-amber-400 dark:hover:bg-amber-950/30"
          onClick={onRemove}
          disabled={anyPending}
        >
          {isRemovePending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Pause className="h-4 w-4" />
          )}
          {t('issues.orderDetail.removeFromFlow')}
        </Button>
      </div>
    </div>
  );
}

function EventHistorySection({
  issue,
  open,
  onOpenChange,
}: {
  issue: ProductionIssue | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('production');
  // Build events from issue metadata
  const events = [];

  if (issue?.created_at) {
    events.push({
      type: 'Issue Created',
      timestamp: issue.created_at,
      description: `${issue.issue_type} issue opened`,
    });
  }

  if (
    issue?.metadata &&
    typeof issue.metadata === 'object' &&
    'awaiting_since' in issue.metadata
  ) {
    events.push({
      type: 'Awaiting Inventory',
      timestamp: issue.metadata.awaiting_since as string,
      description: 'Marked as waiting for inventory',
    });
  }

  // CX notification events
  if (
    issue?.metadata &&
    typeof issue.metadata === 'object' &&
    'cx_notifications' in issue.metadata &&
    Array.isArray(issue.metadata.cx_notifications)
  ) {
    for (const notification of issue.metadata.cx_notifications as Array<Record<string, unknown>>) {
      // BLU-386: legacy entries without `method` predate the discriminator —
      // treat them as 'template' since the auto-template path was the only writer.
      const method = notification.method === 'manual' ? 'manual' : 'template';
      if (method === 'manual') {
        const note = typeof notification.note === 'string' ? notification.note : '';
        events.push({
          type: t('issues.orderDetail.eventHistory.cxMarkedManually'),
          timestamp: notification.sent_at as string,
          description: note.length > 0
            ? t('issues.orderDetail.eventHistory.cxMarkedManuallyNote', { note })
            : t('issues.orderDetail.eventHistory.cxMarkedManuallyNoNote'),
        });
      } else {
        events.push({
          type: t('issues.orderDetail.eventHistory.cxNotified'),
          timestamp: notification.sent_at as string,
          description: t('issues.orderDetail.eventHistory.cxEmailSent', {
            remedy: (notification.remedy_label ?? notification.remedy_value) as string,
          }),
        });
      }
    }
  }

  if (issue?.resolved_at) {
    // Friendly label for the raw resolution_method (Phase 60 added 'bridge_refetch').
    const resolutionLabel =
      issue.resolution_method === 'bridge_refetch'
        ? 'Auto-fetched from source'
        : issue.resolution_method === 'order_terminal'
          ? 'Auto-cleared: order finalized'
          : issue.resolution_method === 'coverage_healed'
            ? 'Auto-cleared: production matches the order again'
            : issue.resolution_method;
    events.push({
      type: issue.resolution_method === 'routed' ? 'Routed' : 'Resolved',
      timestamp: issue.resolved_at,
      description: issue.notes || `Resolution: ${resolutionLabel}`,
    });
  }

  // Sort by timestamp descending
  events.sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  if (events.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between h-12">
          <span className="flex items-center gap-2">
            <Clock className="h-4 w-4" aria-hidden="true" />
            {t('issues.orderDetail.eventHistory')}
          </span>
          {open ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2">
        <div className="space-y-3 pl-4 border-l-2 border-muted ml-2">
          {events.map((event, idx) => (
            <div key={idx} className="relative pl-4">
              <div className="absolute -left-[11px] top-1 w-4 h-4 rounded-full bg-muted flex items-center justify-center">
                <Check className="h-3 w-3 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-medium">{event.type}</p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(event.timestamp), 'MMM d, yyyy h:mm a')}
                </p>
                {event.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {event.description}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
